import { type FC, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth.store";
import { useServicesHealth } from "@/features/services/hooks/useServices";

interface NavItem {
  to: string;
  label: string;
  mark: string;
  meta: string;
  adminOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const allNavGroups: NavGroup[] = [
  {
    label: "Ingestion",
    items: [
      { to: "/upload",    label: "Upload",    mark: "UP", meta: "New evidence" },
      { to: "/downloads", label: "Downloads", mark: "DL", meta: "Queue URLs" },
      { to: "/jobs",      label: "Jobs",      mark: "JB", meta: "Processing" },
      { to: "/documents", label: "Documents", mark: "DC", meta: "Indexed files" },
    ],
  },
  {
    label: "Knowledge",
    items: [
      { to: "/collections", label: "Collections", mark: "CL", meta: "Shared scopes", adminOnly: true },
      { to: "/workspaces",  label: "Workspaces",  mark: "WS", meta: "Private rooms" },
      { to: "/chat",        label: "Chat",        mark: "AI", meta: "Ask documents" },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/services", label: "Services", mark: "SV", meta: "Health status" },
      { to: "/metrics",  label: "Metrics",  mark: "MT", meta: "Stats overview" },
      { to: "/api-keys", label: "API Keys", mark: "AK", meta: "Integrations" },
    ],
  },
];

const adminNavItems: NavItem[] = [
  { to: "/crawler", label: "Crawler", mark: "CR", meta: "Discovery admin" },
  { to: "/admin/users", label: "Users", mark: "US", meta: "Admin access" },
];

export const PageShell: FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const isChat = location.pathname.startsWith("/chat");
  const isFullHeight = isChat || /^\/workspaces\/[^/]+/.test(location.pathname);

  const isAdmin = user?.role === "admin";
  const navGroups: NavGroup[] = allNavGroups
    .filter((group) => isAdmin || group.label !== "Ingestion")
    .map((group) => {
      if (isAdmin) return group;
      if (group.label === "Platform")
        return { ...group, items: group.items.filter((item) => item.to === "/api-keys") };
      return { ...group, items: group.items.filter((item) => !item.adminOnly) };
    })
    .filter((group) => group.items.length > 0);

  // Services health indicator for sidebar
  const { data: servicesData } = useServicesHealth();

  const handleSignOut = () => {
    clearAuth();
    navigate("/login", { replace: true });
  };

  const closeMobile = () => setMobileOpen(false);

  // Determine services dot color
  let servicesDot: "green" | "amber" | "red" | null = null;
  if (servicesData) {
    const total = servicesData.length;
    const healthy = servicesData.filter((s) => s.healthy).length;
    if (total === 0) servicesDot = null;
    else if (healthy === total) servicesDot = "green";
    else if (healthy === 0) servicesDot = "red";
    else servicesDot = "amber";
  }

  return (
    <div className="min-h-screen bg-[#141236] text-slate-100">
      <div className="fixed left-0 right-0 top-0 z-40 flex h-14 items-center gap-3 border-b border-white/10 bg-[#141236]/95 px-4 shadow-sm backdrop-blur md:hidden">
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
          onClick={() => setMobileOpen(true)}
        >
          <span className="flex flex-col gap-1">
            <span className="block h-0.5 w-5 bg-current" />
            <span className="block h-0.5 w-5 bg-current" />
            <span className="block h-0.5 w-5 bg-current" />
          </span>
        </button>
        <span className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-brand text-xs font-bold text-white shadow-lg shadow-brand/20">
            D
          </span>
          <span>
            <span className="block text-sm font-semibold leading-4 text-white">DOCI</span>
            <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Document Intelligence
            </span>
          </span>
        </span>
      </div>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/30 md:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-72 flex-col border-r border-white/10 bg-[#151337] text-white shadow-2xl transition-transform duration-200",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        <div className="border-b border-white/10 px-5 py-5">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-brand text-base font-bold text-white shadow-lg shadow-brand/25">
              D
            </span>
            <span>
              <span className="block text-base font-semibold tracking-wide text-white">
                DOCI
              </span>
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Document Intelligence
              </span>
            </span>
          </div>

          <div className="mt-5 rounded-lg border border-white/10 bg-white/5 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                AI workspace
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                Active
              </span>
            </div>
            <p className="mt-2 text-sm font-medium text-white">
              Document Intelligence console
            </p>
            <p className="mt-1 text-xs leading-5 text-slate-400">
              Search, upload, review, and answer from controlled sources.
            </p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 py-5">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-4">
              <p className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {group.label}
              </p>
              {group.items.map((item) => (
                <SidebarNavItem
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  mark={item.mark}
                  meta={item.meta}
                  onClick={closeMobile}
                  statusDot={item.to === "/services" ? servicesDot : null}
                />
              ))}
            </div>
          ))}

          {isAdmin && (
            <div className="mb-4">
              <p className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Admin
              </p>
              {adminNavItems.map((item) => (
                <SidebarNavItem
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  mark={item.mark}
                  meta={item.meta}
                  onClick={closeMobile}
                  statusDot={null}
                />
              ))}
            </div>
          )}
        </nav>

        <div className="flex-shrink-0 border-t border-white/10 bg-[#100f2b] px-4 py-4">
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/10 text-xs font-bold text-white">
                {getUserInitials(user?.name ?? user?.phone)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">
                  {user?.name ?? user?.phone ?? "User"}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  {user?.role && <Badge variant={user.role}>{user.role}</Badge>}
                  <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    Online
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              className="mt-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <div className={cn(
        "bg-[#17143f] pt-14 md:ml-72 md:pt-0",
        isFullHeight ? "flex h-screen flex-col overflow-hidden" : "min-h-screen",
      )}>
        <main className={cn(
          isFullHeight ? "flex flex-1 flex-col overflow-hidden" : "mx-auto w-full max-w-7xl px-5 py-6 sm:px-8 lg:px-10",
        )}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

interface SidebarNavItemProps {
  to: string;
  label: string;
  mark: string;
  meta: string;
  onClick?: () => void;
  statusDot: "green" | "amber" | "red" | null;
}

const SidebarNavItem: FC<SidebarNavItemProps> = ({ to, label, mark, meta, onClick, statusDot }) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={cn(
        "group relative mb-1.5 flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors",
        isActive
          ? "border border-white/10 bg-white/10 text-white shadow-lg shadow-black/10"
          : "text-slate-300 hover:bg-white/5 hover:text-white"
      )}
    >
      {isActive && (
        <span className="absolute left-0 top-1/2 h-7 w-1 -translate-y-1/2 rounded-r-full bg-brand" />
      )}
      <span
        className={cn(
          "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border text-[11px] font-bold transition-colors",
          isActive
            ? "border-brand/40 bg-brand/20 text-brand"
            : "border-white/10 bg-white/5 text-slate-400 group-hover:border-white/20 group-hover:text-slate-200"
        )}
      >
        {mark}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 font-medium">
          <span className="block truncate">{label}</span>
          {statusDot && (
            <span
              aria-hidden="true"
              className={cn(
                "inline-block h-2 w-2 flex-shrink-0 rounded-full",
                statusDot === "green" && "bg-emerald-400",
                statusDot === "amber" && "bg-amber-400",
                statusDot === "red" && "bg-red-400"
              )}
            />
          )}
        </span>
        <span className="mt-0.5 block truncate text-xs text-slate-500 group-hover:text-slate-400">
          {meta}
        </span>
      </span>
    </NavLink>
  );
};

const getUserInitials = (value?: string | null) => {
  if (!value) return "U";
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "U";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
};
